/**
 * ACT2-1124: the copy of an account of a readable workspace into this sibling workspace (`accountCopies.pick`), for
 * the activation account picker and Settings › Accounts. The first pick copies the account (config-backend copies
 * the secret), and a later pick reuses the copy.
 *
 * - An OAuth account of a platform that config-backend does not copy (`oauth_copy_not_allowed`) needs a new sign-in.
 *   `signInMessage` says so, and the caller offers its own add-account flow (F22c).
 * - A refusal of config-backend's validated create of a warehouse copy says what to do, so `checkRefusal` keeps it
 *   in place instead of a toast.
 * - Any other failure is a toast.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';

import { useOrpc } from '@/_shared/hooks/useOrpc';
import { orpcErrorMessage } from '@/_shared/lib/orpc-errors';

/** The refusals of the validated create of a warehouse account copy (ClickHouse). Each says what to do. */
const COPY_CHECK_REFUSALS = new Set([
  'account_validation_failed',
  'invalid_config',
  'account_type_not_available',
  'validator_unavailable',
]);

function refusalReason(err: unknown): unknown {
  return (err as { data?: { reason?: unknown } } | null)?.data?.reason;
}

function copyCheckRefusal(err: unknown): string | null {
  const reason = refusalReason(err);
  return typeof reason === 'string' && COPY_CHECK_REFUSALS.has(reason) ? orpcErrorMessage(err, '') || null : null;
}

function oauthRefusal(err: unknown): string | null {
  return refusalReason(err) === 'oauth_copy_not_allowed' ? orpcErrorMessage(err, '') || null : null;
}

export interface AccountCopyPick {
  /** Copies the account into this workspace, or reuses its copy. `name` goes into the toast. */
  copy: (accountId: string, name?: string) => void;
  /** The account that a copy runs for now, or null. */
  pickingId: string | null;
  isPending: boolean;
  /** The platform needs a new sign-in (`oauth_copy_not_allowed`), or null. */
  signInMessage: string | null;
  /** Why config-backend refused the validated create of a warehouse copy, or null. */
  checkRefusal: string | null;
}

/** The toast after a copy. */
export function copiedToWorkspace(name: string | undefined): string {
  return name ? `Copied ${name} to this workspace` : 'Copied the account to this workspace';
}

export function useAccountCopyPick(
  options: {
    /** Called with the copy's account id once the account is ready in this workspace. */
    onPicked?: (copyAccountId: string) => void;
    /** Called when config-backend refused the validated create of a warehouse copy. */
    onCheckRefused?: () => void;
  } = {},
): AccountCopyPick {
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  const [signInMessage, setSignInMessage] = useState<string | null>(null);
  const [checkRefusal, setCheckRefusal] = useState<string | null>(null);

  const pick = useMutation(
    orpc.accountCopies.pick.mutationOptions({
      onSuccess: ({ copyAccountId }) => {
        setSignInMessage(null);
        setCheckRefusal(null);
        void queryClient.invalidateQueries({ queryKey: orpc.accountCopies.key() });
        void queryClient.invalidateQueries({ queryKey: orpc.settings.key() });
        options.onPicked?.(copyAccountId);
      },
      onError: (err) => {
        const signIn = oauthRefusal(err);
        if (signIn) {
          setSignInMessage(signIn);
          return;
        }
        const refusal = copyCheckRefusal(err);
        if (refusal) {
          setCheckRefusal(refusal);
          options.onCheckRefused?.();
          return;
        }
        toast.error(orpcErrorMessage(err, 'Could not copy this account'));
      },
    }),
  );

  return {
    copy: (accountId, name) => {
      setCheckRefusal(null);
      // The route takes the id only, so the toast reads the name from this call.
      pick.mutate({ accountId }, { onSuccess: () => toast.success(copiedToWorkspace(name)) });
    },
    pickingId: pick.isPending ? (pick.variables?.accountId ?? null) : null,
    isPending: pick.isPending,
    signInMessage,
    checkRefusal,
  };
}
